import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Wrench,
  ShieldAlert,
  AlertTriangle,
  FileText,
  ClipboardCheck,
  Trash2,
  Pencil,
  Save,
  X,
  Plus,
  StickyNote,
} from "lucide-react";
import { riskLevel, riskLevelLabel, riskLevelClasses } from "@/lib/risk";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function RiskBadge({ level }) {
  return (
    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${riskLevelClasses(level)}`}>
      {riskLevelLabel(level)}
    </span>
  );
}

function AiSourcesBadge({ sources }) {
  if (!sources) return null;
  const added = sources.openai_added || [];
  const modified = sources.openai_modified || [];
  const err = sources.openai_error;
  const models = sources.models || {};
  return (
    <div className="mt-2 border border-indigo-200 bg-indigo-50 rounded p-2 text-xs">
      <div className="flex items-center gap-1 flex-wrap">
        <span className="px-1.5 py-0.5 rounded bg-indigo-600 text-white text-[10px] font-semibold">Dual-AI</span>
        <span className="text-indigo-900 font-semibold">
          Gemini{models.first_pass ? ` (${models.first_pass})` : ""} → GPT{models.critique ? ` (${models.critique})` : ""}
        </span>
        {err && (
          <span className="text-[10px] px-1 py-0.5 rounded bg-amber-200 text-amber-900" title={err}>
            GPT non disponibile — solo Gemini
          </span>
        )}
      </div>
      {(added.length > 0 || modified.length > 0) && (
        <div className="mt-1 text-[11px] text-indigo-900">
          {added.length > 0 && (
            <>
              GPT ha aggiunto: <b>{added.join(", ")}</b>.{" "}
            </>
          )}
          {modified.length > 0 && (
            <>
              GPT ha corretto: <b>{modified.join(", ")}</b>.
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * AttrezzaturaCard — mostra una singola attrezzatura analizzata con modalità visualizza/modifica.
 * L'utente può modificare manualmente TUTTI i campi generati dall'AI (descrizione, rischi, DPI, misure, procedura, ecc.).
 * onSave(next) restituisce l'oggetto aggiornato con flag `_edited: true`.
 */
export default function AttrezzaturaCard({ item, index, onSave, onRemove }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item);

  const startEdit = () => {
    setDraft(JSON.parse(JSON.stringify(item)));
    setEditing(true);
  };
  const cancel = () => {
    setDraft(item);
    setEditing(false);
  };
  const save = () => {
    onSave({ ...draft, _edited: true });
    setEditing(false);
  };

  const setRR = (field, val) => {
    setDraft({ ...draft, riconoscimento: { ...(draft.riconoscimento || {}), [field]: val } });
  };
  const setField = (field, val) => setDraft({ ...draft, [field]: val });

  // Rischi helpers
  const updateRisk = (i, field, val) => {
    const arr = [...(draft.rischi || [])];
    arr[i] = { ...arr[i], [field]: val };
    // Ricalcola R e livello se P/D cambiano
    if (field === "P" || field === "D") {
      const P = Number(arr[i].P) || 0;
      const D = Number(arr[i].D) || 0;
      arr[i].R = String(P * D);
    }
    setDraft({ ...draft, rischi: arr });
  };
  const addRisk = () =>
    setDraft({ ...draft, rischi: [...(draft.rischi || []), { nome: "", P: 1, D: 1, R: "1", descrizione: "" }] });
  const removeRisk = (i) => setDraft({ ...draft, rischi: (draft.rischi || []).filter((_, idx) => idx !== i) });

  // Generic string-list helpers (per DPI, misure, ecc.)
  const updateStrList = (key, i, val) => {
    const arr = [...(draft[key] || [])];
    arr[i] = val;
    setDraft({ ...draft, [key]: arr });
  };
  const addStr = (key) => setDraft({ ...draft, [key]: [...(draft[key] || []), ""] });
  const removeStr = (key, i) =>
    setDraft({ ...draft, [key]: (draft[key] || []).filter((_, idx) => idx !== i) });

  // DPI (oggetti {tipo, norma_en})
  const updateDpi = (i, field, val) => {
    const arr = [...(draft.dpi || [])];
    arr[i] = { ...arr[i], [field]: val };
    setDraft({ ...draft, dpi: arr });
  };
  const addDpi = () => setDraft({ ...draft, dpi: [...(draft.dpi || []), { tipo: "", norma_en: "" }] });
  const removeDpi = (i) => setDraft({ ...draft, dpi: (draft.dpi || []).filter((_, idx) => idx !== i) });

  const view = editing ? draft : item;
  const rr = view.riconoscimento || {};
  const conf = Math.round((rr.confidence || 0) * 100);

  return (
    <div className="border border-border rounded-md p-4 bg-card" data-testid={`s6-card-${index}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          {editing ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div>
                <Label className="text-[10px]">Tipo macchina</Label>
                <Input value={rr.tipo_macchina || ""} onChange={(e) => setRR("tipo_macchina", e.target.value)} />
              </div>
              <div>
                <Label className="text-[10px]">Marca</Label>
                <Input value={rr.marca || ""} onChange={(e) => setRR("marca", e.target.value)} />
              </div>
              <div>
                <Label className="text-[10px]">Modello</Label>
                <Input value={rr.modello || ""} onChange={(e) => setRR("modello", e.target.value)} />
              </div>
              <div>
                <Label className="text-[10px]">Anno</Label>
                <Input value={rr.anno || ""} onChange={(e) => setRR("anno", e.target.value)} />
              </div>
              <div>
                <Label className="text-[10px]">Matricola</Label>
                <Input value={rr.matricola || ""} onChange={(e) => setRR("matricola", e.target.value)} />
              </div>
            </div>
          ) : (
            <>
              <h3 className="font-semibold flex items-center gap-2">
                <Wrench className="w-4 h-4 text-muted-foreground" />
                {rr.tipo_macchina || "Attrezzatura"} — {rr.marca || ""} {rr.modello || ""}
                {item._edited && (
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200"
                    title="Modificato manualmente"
                  >
                    Modificato
                  </span>
                )}
              </h3>
              <div className="text-xs text-muted-foreground mt-0.5">
                {rr.anno && `Anno: ${rr.anno} · `}
                {rr.matricola && `Matricola: ${rr.matricola} · `}
                Confidenza AI: <b>{conf}%</b>
              </div>
              {rr.note_targhetta && (
                <div className="text-xs text-muted-foreground italic mt-1">Targhetta: {rr.note_targhetta}</div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center gap-1">
          {editing ? (
            <>
              <Button
                size="sm"
                variant="default"
                className="h-8 rounded-full"
                onClick={save}
                data-testid={`s6-save-${index}`}
              >
                <Save className="w-3.5 h-3.5 mr-1" /> Salva
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-8"
                onClick={cancel}
                data-testid={`s6-cancel-${index}`}
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm"
                variant="outline"
                className="h-8 rounded-full"
                onClick={startEdit}
                data-testid={`s6-edit-${index}`}
              >
                <Pencil className="w-3.5 h-3.5 mr-1" /> Modifica
              </Button>
              <button
                onClick={onRemove}
                className="text-muted-foreground hover:text-destructive p-1"
                data-testid={`s6-remove-${index}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>

      {item.photos?.length > 0 && (
        <div className="flex gap-2 mt-3">
          {item.photos.map((u) => (
            <img
              key={`photo-${u}`}
              src={`${BACKEND}${u}`}
              alt=""
              className="w-28 h-20 object-cover rounded border border-border"
            />
          ))}
        </div>
      )}

      <AiSourcesBadge sources={item._ai_sources ? { ...item._ai_sources, _gpt_note: item._gpt_note } : null} />
      {item._gpt_note && (
        <div className="mt-2 text-[11px] italic text-muted-foreground border-l-2 border-indigo-400 pl-2">
          GPT-5.5: {item._gpt_note}
        </div>
      )}

      {/* Descrizione */}
      {editing ? (
        <div className="mt-3">
          <Label className="text-[10px]">Descrizione</Label>
          <Textarea
            rows={3}
            value={draft.descrizione || ""}
            onChange={(e) => setField("descrizione", e.target.value)}
            data-testid={`s6-edit-descrizione-${index}`}
          />
        </div>
      ) : (
        view.descrizione && <p className="text-sm mt-3">{view.descrizione}</p>
      )}

      {/* Note operatore (sopralluogo / modalità d'uso) */}
      {editing ? (
        <div className="mt-3">
          <Label className="text-[10px] flex items-center gap-1">
            <StickyNote className="w-3 h-3" /> Note operatore (sopralluogo / modalità d'uso)
          </Label>
          <Textarea
            rows={2}
            placeholder="Note aggiuntive del compilatore per contestualizzare la valutazione…"
            value={draft._operator_note || ""}
            onChange={(e) => setField("_operator_note", e.target.value)}
            data-testid={`s6-edit-note-${index}`}
          />
        </div>
      ) : (
        view._operator_note && (
          <div className="mt-3 border-l-2 border-amber-400 bg-amber-50/50 pl-3 py-2 rounded-r text-sm">
            <div className="text-[11px] uppercase tracking-widest text-amber-800 flex items-center gap-1">
              <StickyNote className="w-3 h-3" /> Note operatore
            </div>
            <div className="mt-1 whitespace-pre-wrap">{view._operator_note}</div>
          </div>
        )
      )}

      {/* Rischi */}
      <div className="mt-3">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
          <ShieldAlert className="w-3 h-3" /> Rischi ({(view.rischi || []).length})
          {editing && (
            <Button size="sm" variant="ghost" className="h-6 ml-1 text-xs" onClick={addRisk}>
              <Plus className="w-3 h-3 mr-0.5" /> aggiungi
            </Button>
          )}
        </div>
        {editing ? (
          <div className="space-y-2 mt-1">
            {(draft.rischi || []).map((r, i) => (
              <div key={`risk-edit-${i}`} className="border border-border rounded p-2 bg-muted/20">
                <div className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-5">
                    <Label className="text-[10px]">Nome</Label>
                    <Input value={r.nome || ""} onChange={(e) => updateRisk(i, "nome", e.target.value)} />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[10px]">P (1-4)</Label>
                    <Input
                      type="number"
                      min={1}
                      max={4}
                      value={r.P || 1}
                      onChange={(e) => updateRisk(i, "P", e.target.value)}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[10px]">D (1-4)</Label>
                    <Input
                      type="number"
                      min={1}
                      max={4}
                      value={r.D || 1}
                      onChange={(e) => updateRisk(i, "D", e.target.value)}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[10px]">R (P×D)</Label>
                    <Input value={r.R || ""} readOnly className="bg-muted/40" />
                  </div>
                  <div className="col-span-1">
                    <Button size="sm" variant="ghost" onClick={() => removeRisk(i)} className="h-9 w-9 p-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <div className="col-span-12">
                    <Label className="text-[10px]">Descrizione</Label>
                    <Textarea
                      rows={2}
                      value={r.descrizione || ""}
                      onChange={(e) => updateRisk(i, "descrizione", e.target.value)}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <ul className="text-sm mt-1 space-y-0.5">
            {(view.rischi || []).map((r, ii) => {
              const lvl = riskLevel(r.P, r.D);
              return (
                <li key={`risk-${r.nome || ii}-${ii}`} className="flex items-start gap-2">
                  <RiskBadge level={lvl} />
                  <div>
                    <b>{r.nome}</b>{" "}
                    <span className="text-[11px] text-muted-foreground">
                      P={r.P} · D={r.D} · R={r.R}
                    </span>
                    {r.descrizione && <div className="text-xs">{r.descrizione}</div>}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* DPI */}
      <div className="mt-3 text-sm">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
          DPI ({(view.dpi || []).length})
          {editing && (
            <Button size="sm" variant="ghost" className="h-6 ml-1 text-xs" onClick={addDpi}>
              <Plus className="w-3 h-3 mr-0.5" /> aggiungi
            </Button>
          )}
        </div>
        {editing ? (
          <div className="space-y-1 mt-1">
            {(draft.dpi || []).map((d, i) => (
              <div key={`dpi-edit-${i}`} className="grid grid-cols-12 gap-2 items-center">
                <Input
                  className="col-span-6"
                  placeholder="Tipo (es. guanti antitaglio)"
                  value={d.tipo || ""}
                  onChange={(e) => updateDpi(i, "tipo", e.target.value)}
                />
                <Input
                  className="col-span-5"
                  placeholder="Norma EN (es. EN 388)"
                  value={d.norma_en || ""}
                  onChange={(e) => updateDpi(i, "norma_en", e.target.value)}
                />
                <Button size="sm" variant="ghost" onClick={() => removeDpi(i)} className="h-9 w-9 p-0 col-span-1">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <ul className="mt-1 space-y-0.5">
            {(view.dpi || []).map((d, ii) => (
              <li key={`dpi-${d.tipo || ii}-${ii}`}>
                • {d.tipo} {d.norma_en && <span className="text-[11px] text-muted-foreground">({d.norma_en})</span>}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Misure preventive */}
      <StrListEditor
        title="Misure preventive"
        list={view.misure_preventive || []}
        editing={editing}
        onUpdate={(i, v) => updateStrList("misure_preventive", i, v)}
        onAdd={() => addStr("misure_preventive")}
        onRemove={(i) => removeStr("misure_preventive", i)}
        placeholder="Es. formazione operatore, protezioni fisse..."
      />

      {/* Procedura operativa */}
      {editing ? (
        <div className="mt-3">
          <Label className="text-[10px]">Procedura operativa</Label>
          <Textarea
            rows={3}
            value={draft.procedura_operativa || ""}
            onChange={(e) => setField("procedura_operativa", e.target.value)}
          />
        </div>
      ) : (
        view.procedura_operativa && (
          <div className="mt-3 text-sm bg-muted/30 rounded p-3">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Procedura operativa</div>
            {view.procedura_operativa}
          </div>
        )
      )}

      {/* Manutenzione (editable ordinaria/straordinaria/periodicita) */}
      {editing ? (
        <div className="mt-3 space-y-2">
          <Label className="text-[10px]">Manutenzione — Ordinaria (una per riga)</Label>
          <Textarea
            rows={2}
            value={(draft.manutenzione?.ordinaria || []).join("\n")}
            onChange={(e) =>
              setDraft({
                ...draft,
                manutenzione: {
                  ...(draft.manutenzione || {}),
                  ordinaria: e.target.value.split("\n").filter((x) => x.trim()),
                },
              })
            }
          />
          <Label className="text-[10px]">Manutenzione — Straordinaria (una per riga)</Label>
          <Textarea
            rows={2}
            value={(draft.manutenzione?.straordinaria || []).join("\n")}
            onChange={(e) =>
              setDraft({
                ...draft,
                manutenzione: {
                  ...(draft.manutenzione || {}),
                  straordinaria: e.target.value.split("\n").filter((x) => x.trim()),
                },
              })
            }
          />
          <Label className="text-[10px]">Periodicità</Label>
          <Input
            value={draft.manutenzione?.periodicita || ""}
            onChange={(e) =>
              setDraft({
                ...draft,
                manutenzione: { ...(draft.manutenzione || {}), periodicita: e.target.value },
              })
            }
          />
        </div>
      ) : (
        view.manutenzione && (
          <div className="mt-3 text-sm">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Manutenzione</div>
            {view.manutenzione.ordinaria?.length > 0 && (
              <div className="text-xs">Ordinaria: {view.manutenzione.ordinaria.join(", ")}</div>
            )}
            {view.manutenzione.straordinaria?.length > 0 && (
              <div className="text-xs">Straordinaria: {view.manutenzione.straordinaria.join(", ")}</div>
            )}
            {view.manutenzione.periodicita && (
              <div className="text-xs">
                Periodicità: <b>{view.manutenzione.periodicita}</b>
              </div>
            )}
          </div>
        )
      )}

      {/* Controlli periodici (read-only per ora, campi troppo specifici) */}
      {view.controlli_periodici?.length > 0 && !editing && (
        <div className="mt-3 text-sm">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
            <ClipboardCheck className="w-3 h-3" /> Controlli periodici obbligatori
          </div>
          <ul className="mt-1 space-y-0.5">
            {view.controlli_periodici.map((c, ii) => (
              <li key={`ctrl-${c.tipo || ii}-${ii}`}>
                • <b>{c.tipo}</b> — {c.periodicita}{" "}
                {c.normativa && <span className="text-[11px] text-muted-foreground">({c.normativa})</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Non conformità */}
      <StrListEditor
        title="Non conformità rilevate"
        list={view.non_conformita_visibili || []}
        editing={editing}
        onUpdate={(i, v) => updateStrList("non_conformita_visibili", i, v)}
        onAdd={() => addStr("non_conformita_visibili")}
        onRemove={(i) => removeStr("non_conformita_visibili", i)}
        placeholder="Es. protezione mancante..."
        variant="danger"
        Icon={AlertTriangle}
      />

      {view.documentazione_ricercabile?.length > 0 && !editing && (
        <div className="mt-3 text-xs text-muted-foreground flex items-center gap-1">
          <FileText className="w-3 h-3" /> Documenti da acquisire: {view.documentazione_ricercabile.join(" · ")}
        </div>
      )}
    </div>
  );
}

function StrListEditor({ title, list, editing, onUpdate, onAdd, onRemove, placeholder, variant, Icon }) {
  if (!editing && !list?.length) return null;
  const containerCls =
    variant === "danger"
      ? "mt-3 border border-rose-200 bg-rose-50 rounded-md p-3"
      : "mt-3 text-sm";
  const titleCls =
    variant === "danger"
      ? "text-[11px] uppercase tracking-widest text-rose-700 flex items-center gap-1"
      : "text-[11px] uppercase tracking-widest text-muted-foreground flex items-center gap-1";
  return (
    <div className={containerCls}>
      <div className={titleCls}>
        {Icon && <Icon className="w-3 h-3" />} {title}
        {editing && (
          <Button size="sm" variant="ghost" className="h-6 ml-1 text-xs" onClick={onAdd}>
            <Plus className="w-3 h-3 mr-0.5" /> aggiungi
          </Button>
        )}
      </div>
      {editing ? (
        <div className="space-y-1 mt-1">
          {list.map((v, i) => (
            <div key={`str-${title}-${i}`} className="grid grid-cols-12 gap-2 items-center">
              <Input
                className="col-span-11"
                placeholder={placeholder}
                value={v}
                onChange={(e) => onUpdate(i, e.target.value)}
              />
              <Button size="sm" variant="ghost" onClick={() => onRemove(i)} className="h-9 w-9 p-0 col-span-1">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <ul className={variant === "danger" ? "mt-1 space-y-0.5 text-sm text-rose-900" : "mt-1 space-y-0.5"}>
          {list.map((v, ii) => (
            <li key={`str-view-${title}-${ii}-${(v || "").slice(0, 20)}`}>• {v}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
